import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const { login, formatApiErrorDetail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="Admin Portal" subtitle="Publisher & content controls" />
      <div className="max-w-xl mx-auto w-full px-4 pb-10">
        <Card className="p-6 sm:p-8 border-slate-200 shadow-xl shadow-slate-200/40">
          <div className="inline-flex h-12 w-12 rounded-2xl bg-slate-900 text-white items-center justify-center mb-3">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h2 className="font-heading font-black text-2xl text-slate-900">Sign in to Dashboard</h2>
          <p className="text-sm text-slate-500 mt-1">
            Authorized publishers only. Sessions expire after 8 hours.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500">Email</Label>
              <Input
                data-testid={ADMIN.loginEmail}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="admin@morningteer.app"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">Password</Label>
              <div className="relative mt-1">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid={ADMIN.loginPass}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-9"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid={ADMIN.loginError}
                className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              data-testid={ADMIN.loginBtn}
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full rounded-full bg-slate-900 hover:bg-slate-800"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>
    </>
  );
}
